import {
  createTheme,
  type CSSVariablesResolver,
  type MantineColorsTuple,
  type MantineThemeOverride,
} from "@mantine/core";

const brandPanelColor = "#120826";

const colors = {
  violet0: "#f5f0ff",
  violet1: "#e7daff",
  violet2: "#cbb2ff",
  violet3: "#ad87ff",
  violet4: "#9463ff",
  violet5: "#844dff",
  violet6: "#7c3aff",
  violet7: "#6a2ce0",
  violet8: "#5b1fd6",
  violet9: "#4b16b8",
  violetHeroSoft: "#b794f6",
  money0: "#e9fbf0",
  money1: "#d1f5df",
  money2: "#a3ebc0",
  money3: "#71e09f",
  money4: "#48d684",
  money5: "#2ecf72",
  money6: "#16a34a",
  money7: "#0f7a37",
  money8: "#0a6b2f",
  money9: "#005522",
  frame: "#211143",
  frameAccent: "#9a70f8",
  ink: "#0b0a12",
  inkHover: "#1a1a22",
  inkSoft: "#43414f",
  inkPublicSoft: "#3d3560",
  muted: "#6c6a7a",
  mutedPublic: "#64748b",
  neutral400: "#9ca3af",
  border: "#e3e3ea",
  borderSubtle: "#e2e8f0",
  borderStrong: "#ccccd6",
  borderPublic: "#e7e3f5",
  page: "#f3f3f6",
  surface: "#ffffff",
  surfaceHover: "#fafafb",
  inputHover: "#ececf0",
  mediaBackground: "#f4f4f5",
  authInput: "#f8fafc",
  authInputHover: "#f1f5f9",
  roleVioletBg: "#f5f3ff",
  roleBlueBg: "#eff6ff",
  cameraBackdrop: "#111111",
  danger: "#dc2626",
  dangerBright: "#ef4444",
  warning: "#f59e0b",
  warningDark: "#d97706",
  warningDarker: "#ca8a04",
  warningBright: "#f97316",
  warningSoftText: "#fff7ed",
  warningAccent: "#fb923c",
  roleTeal: "#0d9488",
  roleTealLight: "#14b8a6",
  roleTealSoft: "#5eead4",
  roleBlue: "#2563eb",
  roleBlueDark: "#1d4ed8",
  roleBlueBright: "#3b82f6",
  roleBlueSoft: "#60a5fa",
  roleBlueLighter: "#7cb8ff",
  roleBlueLightest: "#93c5fd",
  brandPanel: brandPanelColor,
  brandPanelDeep: brandPanelColor,
  whiteAlpha95: "rgba(255, 255, 255, 0.95)",
  whiteAlpha92: "rgba(255, 255, 255, 0.92)",
  whiteAlpha90: "rgba(255, 255, 255, 0.90)",
  whiteAlpha82: "rgba(255, 255, 255, 0.82)",
  whiteAlpha78: "rgba(255, 255, 255, 0.78)",
  whiteAlpha74: "rgba(255, 255, 255, 0.74)",
  whiteAlpha68: "rgba(255, 255, 255, 0.68)",
  whiteAlpha60: "rgba(255, 255, 255, 0.60)",
  whiteAlpha55: "rgba(255, 255, 255, 0.55)",
  whiteAlpha50: "rgba(255, 255, 255, 0.50)",
  whiteAlpha45: "rgba(255, 255, 255, 0.45)",
  whiteAlpha40: "rgba(255, 255, 255, 0.40)",
  whiteAlpha12: "rgba(255, 255, 255, 0.12)",
  whiteAlpha10: "rgba(255, 255, 255, 0.10)",
  whiteAlpha08: "rgba(255, 255, 255, 0.08)",
  whiteAlpha06: "rgba(255, 255, 255, 0.06)",
  whiteAlpha05: "rgba(255, 255, 255, 0.05)",
  violetAlpha50: "rgba(124, 58, 255, 0.50)",
  violetAlpha22: "rgba(124, 58, 255, 0.22)",
  violetAlpha20: "rgba(124, 58, 255, 0.20)",
  violetAlpha18: "rgba(124, 58, 255, 0.18)",
  violetAlpha16: "rgba(124, 58, 255, 0.16)",
  violetAlpha15: "rgba(124, 58, 255, 0.15)",
  violetAlpha14: "rgba(124, 58, 255, 0.14)",
  violetAlpha12: "rgba(124, 58, 255, 0.12)",
  violetAlpha10: "rgba(124, 58, 255, 0.10)",
  violetAlpha06: "rgba(124, 58, 255, 0.06)",
  violetAlpha05: "rgba(124, 58, 255, 0.05)",
  violetAlpha03: "rgba(124, 58, 255, 0.03)",
  violetDarkAlpha28: "rgba(106, 44, 224, 0.28)",
  violetDarkAlpha12: "rgba(106, 44, 224, 0.12)",
  frameAccentAlpha22: "rgba(154, 112, 248, 0.22)",
  frameAccentAlpha18: "rgba(154, 112, 248, 0.18)",
  moneyAlpha20: "rgba(15, 122, 55, 0.20)",
  moneyAlpha18: "rgba(15, 122, 55, 0.18)",
  moneyAlpha10: "rgba(15, 122, 55, 0.10)",
  moneyAlpha08: "rgba(15, 122, 55, 0.08)",
  moneyAlpha06: "rgba(15, 122, 55, 0.06)",
  dangerAlpha20: "rgba(239, 68, 68, 0.20)",
  dangerAlpha05: "rgba(239, 68, 68, 0.05)",
  blueAlpha15: "rgba(37, 99, 235, 0.15)",
  blueAlpha06: "rgba(37, 99, 235, 0.06)",
  blueBorderAlpha50: "rgba(219, 234, 254, 0.50)",
  progressTrack: "rgba(226, 222, 240, 0.60)",
  progressBorder: "rgba(226, 222, 240, 0.50)",
  warningAlpha50: "rgba(249, 115, 22, 0.50)",
  warningAlpha45: "rgba(249, 115, 22, 0.45)",
  warningAlpha18: "rgba(249, 115, 22, 0.18)",
  warningAlpha11: "rgba(249, 115, 22, 0.11)",
  warningAlpha08: "rgba(249, 115, 22, 0.08)",
  warningAlpha0: "rgba(249, 115, 22, 0)",
  warningBorder: "rgba(251, 146, 60, 0.62)",
  warningBorderHover: "rgba(253, 186, 116, 0.85)",
  shadowInk14: "rgba(11, 10, 18, 0.14)",
  shadowInk06: "rgba(11, 10, 18, 0.06)",
  publicInk08: "rgba(20, 12, 40, 0.08)",
  publicInk06: "rgba(20, 12, 40, 0.06)",
  publicInk05: "rgba(20, 12, 40, 0.05)",
  publicInk03: "rgba(20, 12, 40, 0.03)",
  transparent: "transparent",
} as const;

const gradients = {
  authHeroGradient: colors.brandPanel,
  authHeroBlueGradient:
    "linear-gradient(-45deg, #0a0f1a, #0f1e3a, #1e3a8a, #172554, #0a0f1a)",
  authFormGradient:
    "radial-gradient(ellipse at 50% 30%, #f8f9ff 0%, #f0f1fa 60%, #ebedf6 100%)",
  ctaVioletGradient: "linear-gradient(135deg, #7c3aff 0%, #6a2ce0 100%)",
  ctaBlueGradient: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
  roleVioletGradient: "linear-gradient(135deg, #7c3aff 0%, #9463ff 100%)",
  roleTealGradient: "linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)",
  roleBlueGradient: "linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)",
  stepVioletGradient: "linear-gradient(90deg, #7c3aff, #9463ff)",
  stepBlueGradient: "linear-gradient(90deg, #2563eb, #3b82f6)",
  gradientTextViolet: "linear-gradient(135deg, #b794f6 0%, #a780ff 40%, #cbb2ff 100%)",
  gradientTextBlue: "linear-gradient(135deg, #7cb8ff 0%, #60a5fa 40%, #93c5fd 100%)",
  heroOrbViolet: "radial-gradient(circle, rgba(124, 58, 255, 0.22) 0%, transparent 65%)",
  heroOrbDeep: "radial-gradient(circle, rgba(45, 18, 102, 0.35) 0%, transparent 65%)",
  dividerGlow: "radial-gradient(circle, rgba(124, 58, 255, 0.06) 0%, transparent 70%)",
  celebrationShimmer: "linear-gradient(90deg, transparent 0%, rgba(124, 58, 255, 0.15) 50%, transparent 100%)",
  stepShimmer: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.40), transparent)",
  applyPage:
    "radial-gradient(120% 80% at 10% -10%, rgba(124, 58, 255, 0.14), transparent 55%), radial-gradient(90% 60% at 100% 0%, rgba(22, 163, 74, 0.08), transparent 50%), linear-gradient(180deg, #f8f6ff 0%, #f5f0ff 45%, #f0ebff 100%)",
  applyGrid:
    "linear-gradient(rgba(20, 12, 40, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(20, 12, 40, 0.03) 1px, transparent 1px)",
  applyGridMask: "linear-gradient(180deg, #000 0%, transparent 70%)",
  resumeSection: "linear-gradient(165deg, rgba(124, 58, 255, 0.05) 0%, transparent 42%)",
  earningsCompact: "linear-gradient(135deg, rgba(124, 58, 255, 0.06) 0%, rgba(22, 163, 74, 0.05) 100%)",
  earningsFull: "linear-gradient(145deg, var(--mantine-color-violet-0) 0%, var(--mantine-color-grape-0) 100%)",
  termsFade: "linear-gradient(to top, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.50) 50%, transparent 100%)",
  matchCard: "linear-gradient(135deg, #ffffff 0%, #f5f0ff 100%)",
  profileHeader: "linear-gradient(135deg, #faf8ff 0%, #ffffff 70%)",
  pendingIcon: "linear-gradient(135deg, #ede9fe, #ddd6fe)",
} as const;

const units = {
  0: 0, 1: 1, 2: 2, 3: 3, 4: 4, 5: 5, 6: 6, 7: 7, 8: 8, 9: 9, 10: 10,
  11: 11, 12: 12, 13: 13, 14: 14, 15: 15, 16: 16, 17: 17, 18: 18, 20: 20,
  22: 22, 24: 24, 26: 26, 28: 28, 32: 32, 36: 36, 38: 38, 40: 40,
  44: 44, 48: 48, 52: 52, 56: 56, 60: 60, 64: 64, 72: 72, 80: 80, 100: 100,
  120: 120, 140: 140, 148: 148, 160: 160, 170: 170, 180: 180, 200: 200, 220: 220,
  240: 240, 260: 260, 280: 280, 300: 300, 320: 320, 340: 340, 360: 360,
  400: 400, 420: 420, 430: 430, 440: 440, 480: 480, 520: 520, 580: 580,
  600: 600, 640: 640, 720: 720, 760: 760, 768: 768, 800: 800, 820: 820,
  880: 880, 960: 960, 1100: 1100, 1120: 1120, 1400: 1400,
} as const;

const fontSizes = {
  micro: "0.6875rem",
  xs: "0.75rem",
  compact: "0.8125rem",
  currencySymbol: "0.82em",
  sm: "0.84375rem",
  body: "0.875rem",
  bodyStrong: "0.9375rem",
  publicSmall: "0.78rem",
  publicCard: "0.9rem",
  control: "0.95rem",
  md: "1rem",
  subtitle: "1.05rem",
  titleSm: "1.0625rem",
  titleMd: "1.25rem",
  titleLg: "1.375rem",
  publicSuccess: "1.35rem",
  titleXl: "1.625rem",
  title2Xl: "2rem",
  publicHeadline: "clamp(1.45rem, 5.2vw, 1.75rem)",
  authTagline: "clamp(1.625rem, 2.2vw, 2rem)",
} as const;

const fontWeights = { regular: 400, medium: 500, semibold: 600, bold: 700, extraBold: 800 } as const;
const lineHeights = { solid: 1, tight: 1.1, dense: 1.15, compact: 1.2, heading: 1.25, hero: 1.28, title: 1.3, article: 1.35, body: 1.45, relaxed: 1.5, airy: 1.55, loose: 1.6 } as const;
const letterSpacings = { tighter: "-0.03em", tight: "-0.02em", snug: "-0.01em", normal: "0", open: "0.01em", label: "0.04em", caps: "0.06em", section: "0.07em", wide: "0.08em" } as const;
const durations = { instant: "0ms", stagger1: "50ms", progressDelay: "80ms", quick: "100ms", fast: "120ms", panelDelay: "140ms", standard: "150ms", input: "160ms", medium: "200ms", footerDelay: "220ms", passwordDelay: "240ms", stagger5: "250ms", slow: "300ms", loaderPhase: "320ms", stagger7: "350ms", stagger8: "400ms", successPop: "450ms", entrance: "500ms", deliberate: "550ms", celebration: "600ms", loader: "800ms", pulse: "1650ms", glow: "1800ms", shine: "2000ms", shimmer: "3000ms", aurora: "18000ms" } as const;
const easings = { standard: "ease", inOut: "ease-in-out", out: "ease-out", spring: "cubic-bezier(0.34, 1.56, 0.64, 1)" } as const;
const opacities = { hidden: 0, faint: 0.03, subtle: 0.15, iconIdle: 0.35, dimmed: 0.45, disabled: 0.55, muted: 0.58, quiet: 0.75, secondary: 0.85, visible: 1 } as const;
const scales = { popStart: 0.6, successStart: 0.85, rest: 1, active: 1.01, pulse: 1.04, celebrate: 1.08 } as const;
const numbers = { zero: 0, one: 1, two: 2, three: 3, ten: 10, twenty: 20, thirty: 30, overlay: 400 } as const;
const strokeWidths = { subtle: 1.5, regular: 1.75, strong: 2, emphasis: 2.5 } as const;
const percentages = { none: "0%", tenth: "10%", sixteen: "16%", fifth: "20%", third: "30%", thirtyTwo: "32%", twoFifths: "40%", fortyTwo: "42%", authPanel: "44%", half: "50%", threeFifths: "60%", twoThirds: "65%", sevenTenths: "70%", fourFifths: "80%", nineTenths: "90%", full: "100%", double: "200%", quadruple: "400%" } as const;
const dimensions = {
  viewportHeight: "100dvh",
  screenReaderOffset: "-9999em",
  loaderBarWidthSm: "9px",
  loaderBarHeightSm: "22px",
  loaderBarGapSm: "13px",
  loaderBounceSm: "5px",
  loaderBarWidthMd: "13.6px",
  loaderBarHeightMd: "32px",
  loaderBarGapMd: "19.992px",
  loaderBounceMd: "8px",
  loaderBarWidthLg: "16px",
  loaderBarHeightLg: "38px",
  loaderBarGapLg: "24px",
  loaderBounceLg: "10px",
  borderDashed: "1.5px",
  proseCompact: "34ch",
  proseWide: "48ch",
  authCenteredRows: "1fr auto 1fr",
  authHeroPadding: "64px clamp(56px, 10vw, 120px)",
  authFormPadding: "48px clamp(24px, 4vw, 64px)",
  authFormPaddingMobileBlock: `${units[20]}px`,
  authFormPaddingMobileInline: `${units[16]}px`,
  authCardPaddingMobile: `${units[20]}px ${units[16]}px`,
  authSectionPaddingMobile: `${units[16]}px`,
  blurSm: "4px",
  blurMd: "8px",
  blurLg: "10px",
  phonePrefixWidth: "5.5rem",
  proseFoot: "32ch",
  containerPromise: "36rem",
  scrollbarSize: `${units[4]}px`,
} as const;
const shadows = {
  none: "none",
  focus: "0 0 0 3px rgba(124, 58, 255, 0.12)",
  focusStrong: "0 0 0 4px rgba(124, 58, 255, 0.10)",
  popover: "0 8px 24px rgba(11, 10, 18, 0.14)",
  subtle: "0 1px 2px rgba(11, 10, 18, 0.06)",
  termsExpand: "0 4px 12px rgba(124, 58, 255, 0.10)",
  scrollTop: "inset 0 8px 8px -8px rgba(0, 0, 0, 0.06)",
  avatarRing: `0 0 0 3px ${colors.page}`,
} as const;
const transitions = {
  interactive: "background-color 120ms ease, color 120ms ease",
  input: "border-color 200ms ease, box-shadow 200ms ease",
  cta: "transform 200ms ease, background-color 200ms ease",
  all: "all 200ms ease",
  inputFocus: "border-color 150ms ease, box-shadow 150ms ease, background-color 150ms ease",
  nav: "background-color 120ms ease, color 120ms ease",
  row: "background-color 100ms ease",
} as const;

/** Shared with landing CSS and Flutter AxioColors */
const violet: MantineColorsTuple = [
  colors.violet0, colors.violet1, colors.violet2, colors.violet3, colors.violet4,
  colors.violet5, colors.violet6, colors.violet7, colors.violet8, colors.violet9,
];

const money: MantineColorsTuple = [
  colors.money0, colors.money1, colors.money2, colors.money3, colors.money4,
  colors.money5, colors.money6, colors.money7, colors.money8, colors.money9,
];

/**
 * Operations desk tokens.
 * Frame navy = logo navy (#211143). On navy, accent = logo lavender (#9A70F8).
 * On white, accent = UI violet (#7c3aff). Neutrals are gray, not violet-tinted.
 */
export const axioTokens = {
  ...colors,
  ...gradients,
  unit: units,
  fontSize: fontSizes,
  fontWeight: fontWeights,
  lineHeight: lineHeights,
  letterSpacing: letterSpacings,
  duration: durations,
  easing: easings,
  transition: transitions,
  opacity: opacities,
  scale: scales,
  number: numbers,
  strokeWidth: strokeWidths,
  percentage: percentages,
  dimension: dimensions,
  shadow: shadows,
  violet: colors.violet6,
  violetDark: colors.violet8,
  violetLight: colors.violet0,
  money: colors.money6,
  moneyDark: colors.money7,
  moneyLight: colors.money0,

  /* Frame — continuous navy L (header + sidebar) */
  frameHeaderHeight: 72,
  frameLine: colors.whiteAlpha10,
  frameItem: colors.whiteAlpha74,
  frameLabel: colors.whiteAlpha45,
  frameHover: colors.whiteAlpha06,
  frameActiveBg: colors.frameAccentAlpha18,

  /* Content — de-tinted neutrals */
  surfaceElevated: colors.surface,
  white: colors.surface,

  /* Single shadow — popovers only. Cards use borders. */
  shadowPopover: shadows.popover,

  /* Sharp squares everywhere. Avatars / status dots use radiusCircle. */
  radius: { xs: units[0], sm: units[0], md: units[0], lg: units[0], xl: units[0] },
  radiusCircle: 9999,
  spacing: { xs: units[4], sm: units[8], md: units[12], lg: units[16], xl: units[20], xxl: units[24] },
  fontHead: '"Sora", Inter, system-ui, sans-serif',
  fontBody: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
  containerQuery: {
    sm: `${units[400]}px`,
    lg: `${units[720]}px`,
  },
} as const;

function toCssTokenName(name: string) {
  return name.replace(/[A-Z]/g, (character) => `-${character.toLowerCase()}`);
}

function cssVariables(
  namespace: string,
  values: Readonly<Record<string, string | number>>,
  suffix = "",
) {
  return Object.fromEntries(
    Object.entries(values).map(([name, value]) => [
      `--axio-${namespace}-${toCssTokenName(name)}`,
      `${value}${typeof value === "number" ? suffix : ""}`,
    ]),
  );
}

/**
 * CSS files consume the same source-of-truth values as TSX through these
 * generated variables. Do not define visual values in component stylesheets.
 */
export const axioCssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {
    ...cssVariables("color", colors),
    ...cssVariables("gradient", gradients),
    ...cssVariables("unit", units, "px"),
    ...cssVariables("font-size", fontSizes),
    ...cssVariables("font-weight", fontWeights),
    ...cssVariables("line-height", lineHeights),
    ...cssVariables("letter-spacing", letterSpacings),
    ...cssVariables("duration", durations),
    ...cssVariables("easing", easings),
    ...cssVariables("transition", transitions),
    ...cssVariables("opacity", opacities),
    ...cssVariables("scale", scales),
    ...cssVariables("number", numbers),
    ...cssVariables("stroke-width", strokeWidths),
    ...cssVariables("percentage", percentages),
    ...cssVariables("dimension", dimensions),
    ...cssVariables("shadow", shadows),
    "--axio-radius": `${axioTokens.radius.md}px`,
    "--axio-radius-circle": `${axioTokens.radiusCircle}px`,
    "--axio-font-head": axioTokens.fontHead,
    "--axio-font-body": axioTokens.fontBody,
  },
  light: {},
  dark: {},
});

const inputFocusStyles = {
  input: {
    backgroundColor: axioTokens.surface,
    borderColor: axioTokens.border,
    transition: axioTokens.transition.inputFocus,
  },
};

export const theme: MantineThemeOverride = createTheme({
  primaryColor: "violet",
  colors: { violet, money },
  fontFamily: axioTokens.fontBody,
  headings: {
    fontFamily: axioTokens.fontHead,
    fontWeight: `${axioTokens.fontWeight.bold}`,
    sizes: {
      h1: { fontSize: axioTokens.fontSize.titleXl, lineHeight: `${axioTokens.lineHeight.compact}` },
      h2: { fontSize: axioTokens.fontSize.titleMd, lineHeight: `${axioTokens.lineHeight.heading}` },
      h3: { fontSize: axioTokens.fontSize.titleSm, lineHeight: `${axioTokens.lineHeight.title}` },
      h4: { fontSize: axioTokens.fontSize.bodyStrong, lineHeight: `${axioTokens.lineHeight.article}` },
    },
  },
  defaultRadius: axioTokens.radius.md,
  radius: {
    xs: `${axioTokens.radius.xs}`,
    sm: `${axioTokens.radius.sm}`,
    md: `${axioTokens.radius.md}`,
    lg: `${axioTokens.radius.lg}`,
    xl: `${axioTokens.radius.xl}`,
  },
  spacing: {
    xs: `${axioTokens.spacing.xs}px`,
    sm: `${axioTokens.spacing.sm}px`,
    md: `${axioTokens.spacing.md}px`,
    lg: `${axioTokens.spacing.lg}px`,
    xl: `${axioTokens.spacing.xxl}px`,
  },
  other: axioTokens,
  components: {
    Title: {
      defaultProps: {
        style: { fontFamily: axioTokens.fontHead, letterSpacing: "var(--axio-letter-spacing-tight)" },
      },
    },
    Paper: {
      defaultProps: {
        radius: axioTokens.radius.md,
        withBorder: true,
        style: {
          borderColor: axioTokens.border,
          boxShadow: axioTokens.shadow.none,
        },
      },
    },
    Card: {
      defaultProps: {
        radius: axioTokens.radius.md,
        withBorder: true,
        padding: "lg",
        style: {
          borderColor: axioTokens.border,
          boxShadow: axioTokens.shadow.none,
        },
      },
    },
    Button: {
      defaultProps: {
        radius: axioTokens.radius.md,
      },
    },
    ActionIcon: {
      defaultProps: {
        radius: axioTokens.radius.md,
      },
    },
    ThemeIcon: {
      defaultProps: {
        radius: axioTokens.radius.md,
      },
    },
    Avatar: {
      defaultProps: {
        radius: axioTokens.radiusCircle,
      },
    },
    AppShell: {
      styles: {
        main: {
          backgroundColor: axioTokens.page,
          backgroundImage: "none",
        },
        header: {
          borderBottom: `${axioTokens.unit[1]}px solid ${axioTokens.frameLine}`,
          backgroundColor: axioTokens.frame,
          boxShadow: axioTokens.shadow.none,
        },
        navbar: {
          borderRight: `${axioTokens.unit[1]}px solid ${axioTokens.frameLine}`,
          backgroundColor: axioTokens.frame,
        },
      },
    },
    NavLink: {
      styles: {
        root: {
          borderRadius: "var(--axio-radius)",
          transition: axioTokens.transition.nav,
        },
      },
    },
    TextInput: { styles: inputFocusStyles },
    Textarea: { styles: inputFocusStyles },
    Select: { styles: inputFocusStyles },
    MultiSelect: { styles: inputFocusStyles },
    NumberInput: { styles: inputFocusStyles },
    PasswordInput: { styles: inputFocusStyles },
    Table: {
      defaultProps: {
        horizontalSpacing: "md",
        verticalSpacing: "md",
        highlightOnHover: true,
        highlightOnHoverColor: axioTokens.surfaceHover,
      },
      styles: {
        table: {
          borderCollapse: "separate",
          borderSpacing: 0,
        },
        thead: {
          backgroundColor: axioTokens.page,
        },
        th: {
          fontFamily: axioTokens.fontBody,
          fontWeight: "var(--axio-font-weight-semibold)",
          fontSize: axioTokens.fontSize.xs,
          textTransform: "none",
          letterSpacing: axioTokens.letterSpacing.normal,
          color: axioTokens.muted,
          borderBottom: `${axioTokens.unit[1]}px solid ${axioTokens.border}`,
          whiteSpace: "nowrap",
        },
        td: {
          borderBottom: `${axioTokens.unit[1]}px solid ${axioTokens.border}`,
          fontSize: axioTokens.fontSize.sm,
          color: axioTokens.inkSoft,
        },
        tr: {
          transition: axioTokens.transition.row,
        },
      },
    },
    Badge: {
      defaultProps: {
        radius: axioTokens.radius.md,
      },
    },
    Alert: {
      defaultProps: {
        radius: axioTokens.radius.md,
      },
    },
    Tabs: {
      defaultProps: {
        variant: "pills",
        radius: axioTokens.radius.md,
      },
    },
    Modal: {
      defaultProps: {
        radius: axioTokens.radius.md,
        centered: true,
        shadow: "none",
        padding: "lg",
      },
      styles: {
        content: {
          boxShadow: axioTokens.shadowPopover,
          border: `${axioTokens.unit[1]}px solid ${axioTokens.border}`,
        },
        header: {
          paddingBottom: "var(--axio-unit-8)",
        },
        title: {
          fontFamily: axioTokens.fontHead,
          fontWeight: "var(--axio-font-weight-bold)",
          fontSize: axioTokens.fontSize.titleSm,
          letterSpacing: "var(--axio-letter-spacing-tight)",
          color: axioTokens.ink,
        },
        body: {
          paddingTop: "var(--axio-unit-8)",
        },
      },
    },
    Menu: {
      defaultProps: {
        radius: axioTokens.radius.md,
        shadow: "md",
      },
      styles: {
        dropdown: {
          boxShadow: axioTokens.shadowPopover,
          borderColor: axioTokens.border,
        },
      },
    },
    Popover: {
      defaultProps: {
        radius: axioTokens.radius.md,
        shadow: "md",
      },
      styles: {
        dropdown: {
          boxShadow: axioTokens.shadowPopover,
          borderColor: axioTokens.border,
        },
      },
    },
    Tooltip: {
      defaultProps: {
        radius: axioTokens.radius.md,
      },
      styles: {
        tooltip: {
          fontFamily: axioTokens.fontBody,
          fontSize: axioTokens.fontSize.micro,
          fontWeight: axioTokens.fontWeight.medium,
          letterSpacing: axioTokens.letterSpacing.snug,
          lineHeight: `${axioTokens.lineHeight.compact}`,
          padding: `${axioTokens.spacing.xs}px ${axioTokens.spacing.sm}px`,
        },
      },
    },
    Notification: {
      defaultProps: {
        radius: axioTokens.radius.md,
      },
    },
    RadioCard: {
      defaultProps: {
        radius: axioTokens.radius.md,
      },
    },
    Switch: {
      defaultProps: {
        radius: axioTokens.radius.md,
      },
      styles: {
        track: {
          borderRadius: "var(--axio-radius)",
        },
        thumb: {
          borderRadius: "var(--axio-radius)",
        },
      },
    },
    ScrollArea: {
      defaultProps: {
        scrollbarSize: axioTokens.unit[4],
      },
    },
  },
});
